import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  FileText,
  Download,
  Loader2,
  CheckCircle,
  FolderTree,
  AlertTriangle,
} from "lucide-react";
import {
  reportsApi,
  environmentsApi,
  servicesApi,
  groupsApi,
  GenerateAuditResponse,
} from "@/api/client";

export default function Reports() {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string>("");
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>(
    [],
  );
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [includeDrift, setIncludeDrift] = useState(false);
  const [generatedReport, setGeneratedReport] =
    useState<GenerateAuditResponse | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: () => groupsApi.list(),
  });

  const { data: environmentsData } = useQuery({
    queryKey: ["environments"],
    queryFn: () => environmentsApi.list(),
  });

  const { data: servicesData } = useQuery({
    queryKey: ["services"],
    queryFn: () => servicesApi.list(),
  });

  const generateMutation = useMutation({
    mutationFn: reportsApi.generateAudit,
    onSuccess: (response) => {
      setGeneratedReport(response);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    generateMutation.mutate({
      start_date: startDate,
      end_date: endDate,
      group: selectedGroup || undefined,
      environments:
        selectedEnvironments.length > 0 ? selectedEnvironments : undefined,
      services: selectedServices.length > 0 ? selectedServices : undefined,
      format: "pdf",
      include_drift: includeDrift,
    });
  };

  const handleDownload = useCallback(async () => {
    if (!generatedReport) return;

    setIsDownloading(true);
    try {
      const blob = await reportsApi.downloadReport(generatedReport.report_id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `audit-report-${generatedReport.report_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download report:", error);
    } finally {
      setIsDownloading(false);
    }
  }, [generatedReport]);

  const handleEnvironmentToggle = (slug: string) => {
    setSelectedEnvironments((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const handleServiceToggle = (slug: string) => {
    setSelectedServices((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const handleReset = () => {
    setGeneratedReport(null);
    setStartDate("");
    setEndDate("");
    setSelectedGroup("");
    setSelectedEnvironments([]);
    setSelectedServices([]);
    setIncludeDrift(false);
  };

  // Set default dates (last 30 days)
  const getDefaultDates = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return {
      start: thirtyDaysAgo.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    };
  };

  const setDefaultDates = () => {
    const defaults = getDefaultDates();
    setStartDate(defaults.start);
    setEndDate(defaults.end);
  };

  // Get the selected group name for display
  const selectedGroupName = selectedGroup
    ? groupsData?.groups?.find((g) => g.slug === selectedGroup)?.name
    : null;

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generate deployment audit reports
          </p>
        </div>
      </div>

      {/* Success State */}
      {generatedReport && (
        <div className="mt-6 card bg-green-50 border-green-200">
          <div className="flex items-start gap-4">
            <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-gray-900">
                Report Generated Successfully
                {selectedGroupName && (
                  <span className="ml-2 text-sm font-normal text-gray-600">
                    for {selectedGroupName}
                  </span>
                )}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                Your audit report is ready for download.
              </p>

              {/* Summary Stats */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg px-4 py-3 border border-gray-200">
                  <p className="text-sm text-gray-500">Total Deployments</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {generatedReport.summary.total_deployments}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 border border-gray-200">
                  <p className="text-sm text-gray-500">
                    Production Deployments
                  </p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {generatedReport.summary.production_deployments}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 border border-gray-200">
                  <p className="text-sm text-gray-500">Environments</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {generatedReport.summary.environments_included}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-3 border border-gray-200">
                  <p className="text-sm text-gray-500">Services</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {generatedReport.summary.services_deployed}
                  </p>
                </div>
              </div>

              {/* Drift Stats (if included) */}
              {generatedReport.summary.total_drift_events !== undefined &&
                generatedReport.summary.total_drift_events > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg px-4 py-3 border border-yellow-200">
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3 text-yellow-500" />
                        Total Drift Events
                      </p>
                      <p className="text-2xl font-semibold text-gray-900">
                        {generatedReport.summary.total_drift_events}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-3 border border-green-200">
                      <p className="text-sm text-gray-500">Resolved</p>
                      <p className="text-2xl font-semibold text-green-600">
                        {generatedReport.summary.resolved_drift_events ?? 0}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg px-4 py-3 border border-red-200">
                      <p className="text-sm text-gray-500">Unresolved</p>
                      <p className="text-2xl font-semibold text-red-600">
                        {generatedReport.summary.unresolved_drift_events ?? 0}
                      </p>
                    </div>
                  </div>
                )}

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={handleDownload}
                  className="btn btn-primary"
                  disabled={isDownloading}
                >
                  {isDownloading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Downloading...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </>
                  )}
                </button>
                <button onClick={handleReset} className="btn btn-secondary">
                  Generate Another Report
                </button>
              </div>

              <p className="mt-3 text-xs text-gray-500">
                Report expires at{" "}
                {new Date(generatedReport.expires_at).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Report Generation Form */}
      {!generatedReport && (
        <div className="mt-6 card">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="h-6 w-6 text-brand-600" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">
                Deployment Audit Report
              </h2>
              <p className="text-sm text-gray-500">
                Generate a PDF report of deployments within a date range
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date Range */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label">Date Range</label>
                <button
                  type="button"
                  onClick={setDefaultDates}
                  className="text-sm text-brand-600 hover:text-brand-700"
                >
                  Last 30 days
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="start_date" className="sr-only">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    className="input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="end_date" className="sr-only">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    className="input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                  />
                </div>
              </div>
            </div>

            {/* Group Filter */}
            <div>
              <label className="label">
                <FolderTree className="h-4 w-4 inline mr-1" />
                Filter by Group (optional)
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Generate a report for a specific customer or product group
              </p>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="input max-w-md"
              >
                <option value="">All Groups</option>
                {(groupsData?.groups ?? []).map((group) => (
                  <option key={group.id} value={group.slug}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Environment Filter */}
            <div>
              <label className="label">Filter by Environments (optional)</label>
              <p className="text-xs text-gray-500 mb-2">
                Leave empty to include all environments
              </p>
              <div className="flex flex-wrap gap-2">
                {(environmentsData?.environments ?? []).map((env) => (
                  <button
                    key={env.id}
                    type="button"
                    onClick={() => handleEnvironmentToggle(env.slug)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedEnvironments.includes(env.slug)
                        ? "bg-brand-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {env.name}
                    {env.is_production && (
                      <span className="ml-1 text-xs opacity-75">(prod)</span>
                    )}
                  </button>
                ))}
                {(environmentsData?.environments ?? []).length === 0 && (
                  <span className="text-sm text-gray-500">
                    No environments available
                  </span>
                )}
              </div>
            </div>

            {/* Service Filter */}
            <div>
              <label className="label">Filter by Services (optional)</label>
              <p className="text-xs text-gray-500 mb-2">
                Leave empty to include all services
              </p>
              <div className="flex flex-wrap gap-2">
                {(servicesData?.services ?? []).map((svc) => (
                  <button
                    key={svc.id}
                    type="button"
                    onClick={() => handleServiceToggle(svc.slug)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedServices.includes(svc.slug)
                        ? "bg-brand-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {svc.name}
                  </button>
                ))}
                {(servicesData?.services ?? []).length === 0 && (
                  <span className="text-sm text-gray-500">
                    No services available
                  </span>
                )}
              </div>
            </div>

            {/* Include Drift Events */}
            <div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeDrift}
                  onChange={(e) => setIncludeDrift(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                />
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-yellow-500" />
                  <span className="text-sm font-medium text-gray-700">
                    Include Drift Events
                  </span>
                </div>
              </label>
              <p className="mt-1 ml-7 text-xs text-gray-500">
                Add a section showing drift events detected during the report
                period
              </p>
            </div>

            {/* Submit */}
            <div className="flex justify-end pt-4 border-t border-gray-100">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={generateMutation.isPending || !startDate || !endDate}
              >
                {generateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Error State */}
      {generateMutation.isError && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">
            Failed to generate report. Please try again.
          </p>
        </div>
      )}
    </div>
  );
}
